import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw, Save } from 'lucide-react';
import { requestedStockService } from '../../services/requestedStockService';
import { purchaseService } from '../../services/purchaseService';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import PickerPopover from '../../components/PickerPopover';

// =============================================================================
// Requested Stock Codes — procurement's queue.
//
// A planner on the Bill of Materials page needed a component that does not
// exist in md_stock_codes and filed a request instead of inventing an item
// master record. This page is where those requests are completed and pushed.
//
// The grid IS the form: procurement types the missing UoM, item type, product
// class and supplier straight into the row, saves, then approves. Approve is
// the deliberate act — it creates the md_stock_codes record, closes the request
// and (when the request still knows its BOM context) adds the component line
// the planner was trying to add in the first place.
//
// Approve is per row on purpose. There is no "approve all": each of these is a
// new item master record, and the whole reason the table exists is that those
// should not be created in bulk without looking.
// =============================================================================

const ITEM_TYPES = ['Bought-in', 'Work in Progress', 'Finished Good'];

// The fields procurement completes. Everything else on the row is context.
const EDIT_COLS = new Set([
  'stock_code', 'description', 'unit_of_measure', 'item_type',
  'product_class', 'stock_group', 'default_warehouse', 'supplier_code', 'notes',
]);

const COLUMNS = [
  { id: 'status',            title: 'Status',        width: 96 },
  { id: 'stock_code',        title: 'Stock Code',    width: 150 },
  { id: 'description',       title: 'Description',   width: 240 },
  { id: 'unit_of_measure',   title: 'UoM',           width: 74 },
  { id: 'item_type',         title: 'Item Type',     width: 150 },
  { id: 'product_class',     title: 'Product Class', width: 150 },
  { id: 'stock_group',       title: 'Stock Group',   width: 140 },
  { id: 'default_warehouse', title: 'Def. W/H',      width: 110 },
  { id: 'supplier_code',     title: 'Supplier',      width: 170 },
  { id: 'notes',             title: 'Notes',         width: 220 },
  { id: 'parent_code',       title: 'Needed By',     width: 150 },
  { id: 'qty_per',           title: 'Qty Per',       width: 90 },
  { id: 'scrap_pct',         title: 'Scrap %',       width: 84 },
  { id: 'site_code',         title: 'Site',          width: 100 },
  { id: 'requested_by',      title: 'Requested By',  width: 140 },
  { id: 'requested_at',      title: 'Requested',     width: 120 },
  { id: '__approve',         title: '',              width: 44 },
  { id: '__reject',          title: '',              width: 44 },
];
const COL_IDS = COLUMNS.map((c) => c.id);

const T_MUTED = { textDark: '#94a3b8' };
const T_OK = { textDark: '#059669', bgCell: 'rgba(16, 185, 129, 0.10)' };
const T_NO = { textDark: '#e11d48', bgCell: 'rgba(244, 63, 94, 0.10)' };
// A field md_stock_codes will refuse to be created without.
const T_NEEDED = { bgCell: 'rgba(244, 63, 94, 0.08)' };

const STATUS_THEME = { APPROVED: T_OK, REJECTED: T_NO, PENDING: undefined };

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '');

// md_stock_codes cannot be created without these — the approve call refuses,
// so the grid says which ones are missing before anyone clicks.
const REQUIRED = ['stock_code', 'description', 'unit_of_measure', 'item_type'];

const RequestedStockCodes = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showAll, setShowAll] = useState(false);   // pending only, by default
  const [dirty, setDirty] = useState({});          // { request_id: { field: value } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);      // row mid approve/reject
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [picker, setPicker] = useState(null);      // { id, col, options, x, y }

  const rowsRef = useRef([]);
  rowsRef.current = rows;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await requestedStockService.list(showAll ? 'ALL' : 'PENDING');
      setRows(r?.success ? r.data : []);
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load the requests.');
      setRows([]);
    } finally { setLoading(false); }
  }, [showAll]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    purchaseService.getSuppliers()
      .then((r) => { if (r?.success) setSuppliers(r.data); })
      .catch(() => {});
  }, []);

  // Local edit wins over the loaded value until Save.
  const valueOf = useCallback((row, field) => {
    const patch = dirty[row?.request_id];
    return patch && Object.prototype.hasOwnProperty.call(patch, field) ? patch[field] : row?.[field];
  }, [dirty]);

  const missingOn = useCallback(
    (row) => REQUIRED.filter((f) => !String(valueOf(row, f) ?? '').trim()),
    [valueOf]
  );

  const patch = (id, field, value) => {
    setDirty((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    setNote('');
  };

  const supplierLabel = useCallback((code) => {
    if (!code) return '';
    const s = suppliers.find((x) => x.supplier_code === code);
    return s ? `${code} · ${s.supplier_name}` : code;
  }, [suppliers]);

  const getCellContent = useCallback(([col, rowIdx]) => {
    const id = COL_IDS[col];
    const r = rowsRef.current[rowIdx];
    if (!r) return gcell.text('', { readonly: true });
    const pending = r.status === 'PENDING';
    const editable = pending && EDIT_COLS.has(id);
    const missing = pending && REQUIRED.includes(id) && !String(valueOf(r, id) ?? '').trim();

    switch (id) {
      case 'status':
        return gcell.text(r.status, { readonly: true, theme: STATUS_THEME[r.status] });
      case '__approve':
        return gcell.text(pending ? '✓' : '', { readonly: true, theme: pending ? T_OK : T_MUTED });
      case '__reject':
        return gcell.text(pending ? '✕' : '', { readonly: true, theme: pending ? T_NO : T_MUTED });
      case 'supplier_code':
        // Read-only cell + picker, so only a real md_suppliers code can land here.
        return gcell.text(supplierLabel(valueOf(r, id)) || (pending ? '— choose —' : ''), {
          readonly: true, ...(valueOf(r, id) ? {} : { theme: T_MUTED }),
        });
      case 'item_type':
        // Same reason: the md_stock_codes CHECK only accepts three values.
        return gcell.text(String(valueOf(r, id) ?? '') || (pending ? '— choose —' : ''), {
          readonly: true, ...(missing ? { theme: T_NEEDED } : valueOf(r, id) ? {} : { theme: T_MUTED }),
        });
      case 'qty_per':
      case 'scrap_pct':
        return gcell.number(r[id], { readonly: true, theme: T_MUTED });
      case 'requested_at':
        return gcell.text(fmtDate(r.requested_at), { readonly: true, theme: T_MUTED });
      case 'parent_code':
      case 'site_code':
      case 'requested_by':
        return gcell.text(String(r[id] ?? ''), { readonly: true, theme: T_MUTED });
      default:
        return gcell.text(String(valueOf(r, id) ?? ''), {
          readonly: !editable,
          ...(missing ? { theme: T_NEEDED } : {}),
        });
    }
  }, [valueOf, supplierLabel]);

  const onCellEdited = useCallback(([col, rowIdx], newValue) => {
    const id = COL_IDS[col];
    const r = rowsRef.current[rowIdx];
    if (!r || r.status !== 'PENDING' || !EDIT_COLS.has(id)) return;
    if (id === 'supplier_code' || id === 'item_type') return;   // picker-only
    patch(r.request_id, id, newValue.data ?? '');
  }, []);

  const approve = useCallback(async (r) => {
    if (dirty[r.request_id]) {
      setError('Save your changes before pushing this to the item master.');
      return;
    }
    const missing = missingOn(r);
    if (missing.length) {
      setError(`Fill in ${missing.join(', ')} before approving.`);
      return;
    }
    if (!window.confirm(
      `Create "${r.stock_code}" in the item master?\n\n`
      + 'This adds a real stock code every module can use, and closes the request.'
    )) return;

    setBusyId(r.request_id); setError(''); setNote('');
    try {
      const res = await requestedStockService.approve(r.request_id);
      setNote(res?.message || `${r.stock_code} created.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not create the stock code.');
    } finally { setBusyId(null); }
  }, [dirty, missingOn, load]);

  const reject = useCallback(async (r) => {
    const why = window.prompt(`Reject the request for "${r.stock_code}"?\n\nReason (shown to the requester):`, '');
    if (why === null) return;   // cancelled
    setBusyId(r.request_id); setError(''); setNote('');
    try {
      await requestedStockService.reject(r.request_id, why);
      setNote(`Rejected ${r.stock_code}.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not reject this request.');
    } finally { setBusyId(null); }
  }, [load]);

  const onCellClicked = useCallback(([col, rowIdx], event) => {
    const id = COL_IDS[col];
    const r = rowsRef.current[rowIdx];
    if (!r || busyId) return;
    if (r.status !== 'PENDING') return;

    if (id === '__approve') { approve(r); return; }
    if (id === '__reject') { reject(r); return; }

    if (id === 'supplier_code' || id === 'item_type') {
      const b = event?.bounds;
      setPicker({
        id: r.request_id,
        col: id,
        options: id === 'supplier_code'
          ? suppliers.map((s) => ({ code: s.supplier_code, description: s.supplier_name, hint: s.currency_code || '' }))
          : ITEM_TYPES.map((t) => ({ code: t, label: t })),
        x: b ? b.x : 200,
        y: b ? b.y + b.height : 200,
      });
    }
  }, [busyId, approve, reject, suppliers]);

  const dirtyCount = Object.keys(dirty).length;

  const save = async () => {
    if (!dirtyCount || saving) return;
    setSaving(true); setError(''); setNote('');
    const failures = [];
    for (const [id, fields] of Object.entries(dirty)) {
      try {
        await requestedStockService.update(id, fields);
      } catch (e) {
        failures.push(e.response?.data?.message || e.message);
      }
    }
    setSaving(false);
    if (failures.length) setError(`${failures.length} request(s) failed — ${failures[0]}`);
    else setNote(`Saved ${dirtyCount} request${dirtyCount === 1 ? '' : 's'}.`);
    await load();
  };

  const pendingCount = useMemo(() => rows.filter((r) => r.status === 'PENDING').length, [rows]);
  const blockedCount = useMemo(
    () => rows.filter((r) => r.status === 'PENDING' && missingOn(r).length > 0).length,
    [rows, missingOn]
  );

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-5 pb-3">
        <button onClick={() => navigate('/purchase')}
          className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">
          <ArrowLeft className="w-3.5 h-3.5" /> Purchase
        </button>
        <h1 className="m-0 mt-1 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Requested Stock Codes
        </h1>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          Stock codes planners need but the item master does not have yet. Complete the row, save, then
          approve — approving creates the record in <span className="font-mono">md_stock_codes</span> and,
          where the request came from a bill of materials, adds the component line it was needed for.
        </p>
      </div>

      <div className="shrink-0 px-6 pb-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setShowAll((v) => !v)}
          className="text-xs px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
          {showAll ? 'Show pending only' : 'Show reviewed too'}
        </button>
        <button type="button" onClick={load} disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>

        <span className="text-xs text-slate-500 dark:text-slate-400">
          {pendingCount} pending
          {blockedCount > 0 && (
            <span className="text-red-600 dark:text-red-400"> · {blockedCount} missing required fields</span>
          )}
        </span>

        {note && <span className="ml-auto text-xs font-medium text-emerald-600 dark:text-emerald-400">{note}</span>}
        {dirtyCount > 0 && <span className={`${note ? '' : 'ml-auto'} text-xs text-amber-600 dark:text-amber-400`}>{dirtyCount} unsaved</span>}
        <button type="button" onClick={save} disabled={!dirtyCount || saving}
          className={`${note || dirtyCount ? '' : 'ml-auto'} inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white ${
            dirtyCount && !saving ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600 cursor-default'}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>

      {error && (
        <div className="shrink-0 mx-6 mb-3 rounded-md border border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 px-6 pb-6">
        {loading ? (
          <div className="h-40 flex items-center justify-center text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="h-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center">
            <p className="m-0 px-8 text-center text-sm text-slate-400">
              {showAll ? 'No requests have been raised yet.' : 'Nothing waiting — no stock codes have been requested.'}<br />
              <span className="text-xs">
                Planners raise these from Manufacturing → Bill of Materials → Add component → Request a new stock code.
              </span>
            </p>
          </div>
        ) : (
          <MantleDataGrid
            gridId="requested-stock-codes"
            columns={COLUMNS}
            rowCount={rows.length}
            getCellContent={getCellContent}
            onCellEdited={onCellEdited}
            onCellClicked={onCellClicked}
            freezeColumns={2}
            hint="Pink = required before this can be pushed to the item master · Item Type and Supplier open a picker · ✓ creates the stock code, ✕ rejects the request with a reason."
          />
        )}
      </div>

      {/* Item Type / Supplier picker, anchored under the clicked cell. */}
      {picker && (
        <PickerPopover
          anchor={picker}
          options={picker.options}
          value={(() => {
            const r = rows.find((x) => x.request_id === picker.id);
            return r ? valueOf(r, picker.col) : undefined;
          })()}
          onPick={(code) => { patch(picker.id, picker.col, code); setPicker(null); }}
          onClear={() => { patch(picker.id, picker.col, ''); setPicker(null); }}
          clearLabel="Clear"
          placeholder={picker.col === 'supplier_code' ? 'Filter suppliers…' : 'Filter…'}
          emptyText={picker.col === 'supplier_code' && suppliers.length === 0
            ? 'No suppliers yet — add them in Purchase > Suppliers.'
            : 'No match.'}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
};

export default RequestedStockCodes;
